<!-- Profile → Settings' VIDEOS (2026-10-20, Seán's Design 1 and 2; restyled 2026-10-23, his
     spec): the heading and "You have access to:" above a card like the other sections', one row
     per collection: the access, stated in the positive (never a count of locked videos: Seán,
     2026-11-02, fullstack claude/VIDEOS.md § Access); where every one plays, a tick and "All
     unlocked" (green); limited access, "for 2026" and "Until <date>". Read only: no switches,
     nothing clickable (useLockedVideos.js). -->
<template>
  <section class="settingsSection" aria-labelledby="settings-videos">
    <h3 id="settings-videos" class="heading">
      <span class="title">Videos</span>
      <span class="description">You have access to:</span>
      <!-- how access works, by the account's access (Seán's spec, 2026-10-23); no counts: the
           rows say them -->
      <AppHelpPopup
        id="videosHelp"
        class="videosHelp"
        label="About your video access"
        is-in-dialog
        bounds-selector=".content">
        <span v-if="accessState === 'none'" class="helpParagraph">
          Register for a VIZBI <i>Online</i> Workshop or Masterclass to unlock ongoing access to
          the corresponding videos.
        </span>
        <!-- the next step only, the event(s) named once (Seán, 2026-10-23): the rows already say
             what's unlocked -->
        <span v-else-if="accessState === 'partial'" class="helpParagraph">
          Register for the VIZBI <i>Online</i> {{ lockedNames }} to unlock ongoing access to all
          videos up to 2026.
        </span>
        <span v-else class="helpParagraph">
          You have ongoing access to all VIZBI videos up to 2026.
        </span>
        <span class="helpParagraph">
          To access videos from 2027 onwards, you'll need to register for a future VIZBI event.
        </span>
      </AppHelpPopup>
    </h3>
    <div class="card">
      <div v-for="line in lines" :key="line.collection" class="row" :data-videos="line.collection">
        <!-- narrow, 1px smaller; on a 375px phone "Workshop up to 2026", on a 320px one
             "Workshop to 2026" (the card's container queries): the year always kept -->
        <!-- limited access (Seán, 2026-11-02): this year's videos and when the access ends, in
             place of the paid years (not mentioned) -->
        <span v-if="line.limited" class="access">
          {{ line.name }}<span class="videos"> videos</span> for {{ line.limited.year }}
        </span>
        <span v-else class="access">
          {{ line.name }}<span class="videos"> videos</span><span class="up"> up</span>
          to {{ line.until }}
        </span>
        <span v-if="line.limited" class="status unlocked">Until {{ line.limited.date }}</span>
        <!-- never a count of locked videos: access is stated in the positive (Seán, 2026-11-02;
             fullstack claude/VIDEOS.md § Access) -->
        <span v-else-if="line.isAllUnlocked" class="status unlocked">
          <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4.5 12.5l5 5 10-11" />
          </svg>
          All unlocked
        </span>
      </div>
    </div>
  </section>
</template>

<script setup>
import {computed} from "vue";
import AppHelpPopup from "@/components/App/HelpPopup.vue";

// lockedCounts: {workshop, masterclass}, null until the catalogue has loaded
// limited: [{collection, year, until (ms)}], each limited access while it lasts
const props = defineProps(["videoAccess", "lockedCounts", "limited"]);
const UNTIL = {
  workshop: ["Workshop", "workshopVideosUntil", 2014],
  masterclass: ["Masterclass", "masterclassVideosUntil", 2011],
};

const TWELVE_HOURS = 12 * 3600 * 1000;
// the last day of the access, anywhere on Earth: an end at 12:00 UTC is midnight at UTC−12, so
// the day before it ends ("15 November" for 2026-11-16T12:00Z)
function getLastDay(until) {
  const lastMoment = new Date(until - TWELVE_HOURS - 1);
  return lastMoment.toLocaleDateString("en-GB", {day: "numeric", month: "long", timeZone: "UTC"});
}

const lines = computed(() => Object.entries(UNTIL).map(([collection, [name, field, start]]) => {
  const until = props.videoAccess?.[field] ?? start;
  const locked = props.lockedCounts?.[collection] || 0;
  const isAllUnlocked = Boolean(props.lockedCounts) && !locked;
  const grant = (props.limited || []).find((entry) => entry.collection === collection);
  const limited = grant?.year > until ? {year: grant.year, date: getLastDay(grant.until)} : null;
  return {collection, name, until, locked, isAllUnlocked, limited};
}));

// partial access: the events still to register for ("Masterclass", "Workshop and Masterclass")
const lockedNames = computed(() => {
  return lines.value.filter((line) => !line.isAllUnlocked).map((line) => line.name).join(" and ");
});

// the help's message: "full" once every video plays, "none" without any paid access (no
// …VideosUntil in the account), else "partial"
const accessState = computed(() => {
  if (lines.value.every((line) => line.isAllUnlocked)) return "full";
  const fields = Object.values(UNTIL).map(([, field]) => field);
  return fields.some((field) => props.videoAccess?.[field]) ? "partial" : "none";
});
</script>

<style scoped>
.settingsSection {
  margin-top: var(--section-gap, 12px); /* as SettingsSwitches.vue */
}
/* as SettingsSwitches.vue's heading */
.heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0 0 5px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
}
.title {
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #1a1a1a;
}
.description {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.55);
}
/* as PRIVACY's "?" (SettingsPrivacy.vue): a filled grey circle with a white mark */
.videosHelp {
  --help-color: #8a8a8a;
  font-size: 15px;
}
.videosHelp :deep(button.helpButton) {
  border-color: var(--help-color);
  background: var(--help-color);
  color: #fff;
}
/* the card and its rows: as SettingsSwitches.vue's (white, the border, the corners, the inset
   line between rows), without its pointer: nothing here is clickable */
.card {
  container-type: inline-size; /* the rows' words follow its width */
  background: #fcfcfc;
  border: 1px solid #e3e3e3;
  border-radius: 12px;
}
.row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px 0 12px;
  font-size: 14px;
  color: #222;
  white-space: nowrap; /* one line each, the status beside it */
}
.row + .row::before {
  content: "";
  position: absolute;
  top: 0;
  left: 10px;
  right: 10px;
  border-top: 1px solid #e6e6e6;
}
.access {
  overflow: hidden;
  text-overflow: ellipsis;
}
.status {
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: 4px;
  font-size: 13px;
}
/* every video plays: the dark brand green, as text (no underline, no pointer: not a link) */
.unlocked {
  color: var(--brand-dark-color);
}
.icon {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
/* an iPhone's width (390px and up): 1px smaller, the words kept (Seán, 2026-10-23); a 375px
   phone's: "Workshop up to 2026"; a 320px phone's: "Workshop to 2026" */
@container (max-width: 320px) {
  .row {
    font-size: 13px;
  }
  .status {
    font-size: 12px;
  }
}
@container (max-width: 300px) {
  .videos {
    display: none;
  }
}
@container (max-width: 250px) {
  .up {
    display: none;
  }
}
@media (max-height: 600px) {
  .heading {
    margin-bottom: 3px;
  }
  .row {
    min-height: 28px;
  }
}
</style>
